import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Receipt, Trash, DownloadSimple, MagnifyingGlass, Percent, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { quotesApi, debtorApi, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { fmtCurrency as fmt } from "@/lib/format";

const EMPTY_CLIENT = { nombre: "", ruc: "", email: "", telefono: "", direccion: "" };
const EMPTY_ITEM = { descripcion: "", cantidad: 1, precio_unitario: 0 };

function computeTotals(items, iva_rate, descuento, iva_incluido = true) {
  // raw = suma cant * precio_unit (con IVA incluido si iva_incluido=true).
  const raw = items.reduce(
    (s, it) => s + (Number(it.cantidad) || 0) * (Number(it.precio_unitario) || 0),
    0
  );
  const rate = Number(iva_rate) || 0;
  if (iva_incluido) {
    const basePre = rate ? raw / (1 + rate / 100) : raw;
    const desc = Math.min(Number(descuento) || 0, basePre);
    const base = Math.max(0, basePre - desc);
    const iva = base * (rate / 100);
    const total = base + iva;
    // subtotal se muestra como el "total con IVA" ingresado por el usuario (raw).
    return { subtotal: raw, desc, base, iva, total };
  }
  const subtotal = raw;
  const desc = Math.min(Number(descuento) || 0, subtotal);
  const base = Math.max(0, subtotal - desc);
  const iva = base * (rate / 100);
  const total = base + iva;
  return { subtotal, desc, base, iva, total };
}

function downloadFile(url) {
  const a = document.createElement("a");
  a.href = url;
  a.rel = "noopener";
  a.download = "";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export default function QuotesPage() {
  const [quotes, setQuotes] = useState([]);
  const [debtors, setDebtors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [q, d] = await Promise.all([
        quotesApi.list(),
        debtorApi.list().catch(() => []),
      ]);
      setQuotes(q);
      setDebtors(d);
    } catch {
      toast.error("No se pudieron cargar las cotizaciones");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!query) return quotes;
    const q = query.toLowerCase();
    return quotes.filter((it) =>
      [it.numero, it.cliente?.nombre, it.cliente?.ruc, it.cliente?.email]
        .some((v) => (v || "").toString().toLowerCase().includes(q))
    );
  }, [quotes, query]);

  const handleDelete = async (q) => {
    if (!window.confirm(`¿Eliminar la cotización ${q.numero}?`)) return;
    try {
      await quotesApi.remove(q.id);
      toast.success("Cotización eliminada");
      load();
    } catch { toast.error("No se pudo eliminar"); }
  };

  const handlePdf = (q) => downloadFile(quotesApi.pdfUrl(q.id));

  return (
    <div className="space-y-6" data-testid="quotes-page">
      <div className="flex flex-col md:flex-row gap-3 md:items-center justify-between">
        <div className="relative w-full md:w-80">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="search-quotes"
            placeholder="Buscar por número, cliente, RUC…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <NewQuoteDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          debtors={debtors}
          onCreated={load}
        />
      </div>

      <div className="bg-card border border-border rounded-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-muted-foreground">Cargando…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center" data-testid="quotes-empty">
            <div className="w-16 h-16 rounded-md bg-primary/10 grid place-items-center mx-auto mb-4 text-primary">
              <Receipt size={30} weight="duotone" />
            </div>
            <h3 className="font-heading text-xl font-semibold">Aún no has creado cotizaciones</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
              Crea tu primera cotización con productos, IVA y descuento. Podrás descargarla en PDF listo para enviar.
            </p>
            <Button className="mt-5" onClick={() => setDialogOpen(true)} data-testid="quotes-empty-cta">
              <Plus size={16} weight="bold" className="mr-2" /> Nueva cotización
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
          <Table className="min-w-[720px]">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>N° / Fecha</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Ítems</TableHead>
                <TableHead>IVA</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((q) => (
                <TableRow key={q.id} data-testid={`quote-row-${q.id}`}>
                  <TableCell>
                    <div className="font-mono font-medium">{q.numero}</div>
                    <div className="text-xs text-muted-foreground">{q.fecha_iso}</div>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{q.cliente?.nombre}</div>
                    <div className="text-xs text-muted-foreground">{q.cliente?.ruc || q.cliente?.email || "—"}</div>
                  </TableCell>
                  <TableCell className="text-sm">{q.items?.length || 0} ítem{(q.items?.length || 0) === 1 ? "" : "s"}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="rounded-full">
                      {q.iva_rate}%
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-mono font-semibold">{fmt(q.total)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handlePdf(q)}
                        data-testid={`quote-pdf-${q.id}`}
                        title="Descargar PDF"
                      >
                        <DownloadSimple size={14} weight="bold" className="mr-1" /> PDF
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(q)}
                        data-testid={`quote-delete-${q.id}`}
                        className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                      >
                        <Trash size={14} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        )}
      </div>
    </div>
  );
}

function NewQuoteDialog({ open, onOpenChange, debtors, onCreated }) {
  const [debtorId, setDebtorId] = useState("");
  const [cliente, setCliente] = useState(EMPTY_CLIENT);
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [ivaRate, setIvaRate] = useState(15);
  const [ivaIncluido, setIvaIncluido] = useState(true);
  const [descuento, setDescuento] = useState(0);
  const [validez, setValidez] = useState(15);
  const [notas, setNotas] = useState("");
  const [saving, setSaving] = useState(false);

  const totals = useMemo(() => computeTotals(items, ivaRate, descuento, ivaIncluido), [items, ivaRate, descuento, ivaIncluido]);

  const reset = () => {
    setDebtorId("");
    setCliente(EMPTY_CLIENT);
    setItems([{ ...EMPTY_ITEM }]);
    setIvaRate(15);
    setIvaIncluido(true);
    setDescuento(0);
    setValidez(15);
    setNotas("");
  };

  const handleOpenChange = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const pickDebtor = (id) => {
    setDebtorId(id);
    if (id === "" || id === "__new__") {
      setCliente(EMPTY_CLIENT);
      return;
    }
    const d = debtors.find((x) => x.id === id);
    if (d) {
      setCliente({
        nombre: d.cliente_nombre || "",
        ruc: d.ruc || "",
        email: d.email || "",
        telefono: d.telefono || "",
        direccion: d.direccion || "",
      });
    }
  };

  const setItem = (idx, patch) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const addItem = () => setItems((prev) => [...prev, { ...EMPTY_ITEM }]);
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (!cliente.nombre.trim()) return toast.warning("Ingresa el nombre del cliente");
    const validItems = items.filter(
      (it) => it.descripcion.trim() && Number(it.cantidad) > 0 && Number(it.precio_unitario) >= 0
    );
    if (validItems.length === 0) return toast.warning("Añade al menos un ítem válido");

    setSaving(true);
    try {
      const created = await quotesApi.create({
        cliente,
        debtor_id: debtorId && debtorId !== "__new__" ? debtorId : "",
        items: validItems.map((it) => ({
          descripcion: it.descripcion.trim(),
          cantidad: Number(it.cantidad),
          precio_unitario: Number(it.precio_unitario),
        })),
        iva_rate: Number(ivaRate),
        iva_incluido: !!ivaIncluido,
        descuento: Number(descuento),
        validez_dias: Number(validez),
        notas: notas.trim(),
      });
      toast.success(`Cotización ${created.numero} creada`);
      handleOpenChange(false);
      onCreated?.();
      // Auto-descarga PDF
      setTimeout(() => downloadFile(quotesApi.pdfUrl(created.id)), 300);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo crear la cotización");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button data-testid="open-new-quote-btn">
          <Plus size={16} weight="bold" className="mr-2" /> Nueva cotización
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="new-quote-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Nueva cotización</DialogTitle>
          <DialogDescription>
            Selecciona un cliente existente o crea uno nuevo. Los totales se calculan automáticamente.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-5">
          {/* Cliente */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Cliente</Label>
              {debtors.length > 0 && (
                <div className="w-64">
                  <Select value={debtorId || "__new__"} onValueChange={pickDebtor}>
                    <SelectTrigger data-testid="pick-debtor"><SelectValue placeholder="Cliente nuevo" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__new__">➕ Cliente nuevo</SelectItem>
                      {debtors.slice(0, 100).map((d) => (
                        <SelectItem key={d.id} value={d.id}>{d.cliente_nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="c-nombre">Nombre / Razón social <span className="text-destructive">*</span></Label>
                <Input id="c-nombre" value={cliente.nombre} onChange={(e) => setCliente({ ...cliente, nombre: e.target.value })} required data-testid="c-nombre" />
              </div>
              <div>
                <Label htmlFor="c-ruc">RUC / Cédula</Label>
                <Input id="c-ruc" value={cliente.ruc} onChange={(e) => setCliente({ ...cliente, ruc: e.target.value })} data-testid="c-ruc" />
              </div>
              <div>
                <Label htmlFor="c-email">Correo</Label>
                <Input id="c-email" type="email" value={cliente.email} onChange={(e) => setCliente({ ...cliente, email: e.target.value })} data-testid="c-email" />
              </div>
              <div>
                <Label htmlFor="c-tel">Teléfono</Label>
                <Input id="c-tel" value={cliente.telefono} onChange={(e) => setCliente({ ...cliente, telefono: e.target.value })} data-testid="c-tel" />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="c-dir">Dirección</Label>
                <Input id="c-dir" value={cliente.direccion} onChange={(e) => setCliente({ ...cliente, direccion: e.target.value })} data-testid="c-direccion" />
              </div>
            </div>
          </div>

          {/* Items */}
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Productos / Servicios</Label>
            <div className="border border-border rounded-md overflow-x-auto">
              <div className="min-w-[560px]">
              <div className="grid grid-cols-[1fr_80px_120px_120px_40px] gap-2 px-3 py-2 bg-muted/40 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                <span>Descripción</span>
                <span className="text-right">Cant.</span>
                <span className="text-right">{ivaIncluido ? "P. unit. c/IVA" : "P. unit."}</span>
                <span className="text-right">{ivaIncluido ? "Total c/IVA" : "Subtotal"}</span>
                <span></span>
              </div>
              {items.map((it, idx) => {
                const sub = (Number(it.cantidad) || 0) * (Number(it.precio_unitario) || 0);
                return (
                  <div key={idx} className="grid grid-cols-[1fr_80px_120px_120px_40px] gap-2 px-3 py-2 items-center border-t border-border" data-testid={`item-row-${idx}`}>
                    <Input
                      value={it.descripcion}
                      onChange={(e) => setItem(idx, { descripcion: e.target.value })}
                      placeholder="Ej: Consultoría contable"
                      data-testid={`item-desc-${idx}`}
                    />
                    <Input
                      type="number"
                      step="1"
                      min="0"
                      value={it.cantidad}
                      onChange={(e) => setItem(idx, { cantidad: e.target.value })}
                      className="text-right"
                      data-testid={`item-qty-${idx}`}
                    />
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={it.precio_unitario}
                      onChange={(e) => setItem(idx, { precio_unitario: e.target.value })}
                      className="text-right"
                      data-testid={`item-price-${idx}`}
                    />
                    <div className="text-right font-mono text-sm py-2 pr-1">{fmt(sub)}</div>
                    <button
                      type="button"
                      onClick={() => removeItem(idx)}
                      disabled={items.length === 1}
                      data-testid={`item-remove-${idx}`}
                      className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Quitar ítem"
                    >
                      <X size={14} weight="bold" />
                    </button>
                  </div>
                );
              })}
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addItem}
              data-testid="add-item-btn"
              className="w-full"
            >
              <Plus size={14} weight="bold" className="mr-1" /> Agregar ítem
            </Button>
          </div>

          {/* IVA + Descuento + Validez */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>IVA</Label>
              <Select value={String(ivaRate)} onValueChange={(v) => setIvaRate(Number(v))}>
                <SelectTrigger data-testid="iva-rate"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0%  (bienes exentos)</SelectItem>
                  <SelectItem value="15">15% (tarifa general Ecuador)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Modo de precios</Label>
              <Select
                value={ivaIncluido ? "incluido" : "sin_iva"}
                onValueChange={(v) => setIvaIncluido(v === "incluido")}
              >
                <SelectTrigger data-testid="iva-incluido"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="incluido">Precios con IVA incluido</SelectItem>
                  <SelectItem value="sin_iva">Precios sin IVA</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground mt-1">
                {ivaIncluido
                  ? "Ingresas el total con IVA — el sistema calcula la base."
                  : "Ingresas el precio sin IVA — el sistema añade el IVA."}
              </p>
            </div>
            <div>
              <Label htmlFor="descuento">Descuento (USD)</Label>
              <div className="relative">
                <Percent size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="descuento"
                  type="number"
                  step="0.01"
                  min="0"
                  value={descuento}
                  onChange={(e) => setDescuento(e.target.value)}
                  className="pl-8"
                  data-testid="descuento"
                />
              </div>
            </div>
            <div className="md:col-span-3">
              <Label htmlFor="validez">Validez (días)</Label>
              <Input
                id="validez"
                type="number"
                min="1"
                max="365"
                value={validez}
                onChange={(e) => setValidez(e.target.value)}
                data-testid="validez"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="notas">Notas / Términos</Label>
            <Textarea
              id="notas"
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              rows={2}
              placeholder="Ej: 50% al aprobar, 50% al entregar. Precios sin transporte."
              data-testid="notas"
            />
          </div>

          {/* Totales */}
          <div className="bg-accent/30 border border-border rounded-md p-4 space-y-1.5" data-testid="quote-totals-panel">
            {(ivaIncluido ? [
              [`Total ingresado (c/IVA)`, totals.subtotal],
              ["Descuento", -totals.desc],
              ["Base imponible", totals.base],
              [`IVA (${ivaRate}%)`, totals.iva],
            ] : [
              ["Subtotal", totals.subtotal],
              ["Descuento", -totals.desc],
              ["Base imponible", totals.base],
              [`IVA (${ivaRate}%)`, totals.iva],
            ]).map(([label, val]) => (
              <div key={label} className="flex justify-between text-sm">
                <span className="text-muted-foreground">{label}</span>
                <span className="font-mono">{fmt(val)}</span>
              </div>
            ))}
            <div className="flex justify-between pt-2 mt-2 border-t border-border">
              <span className="font-heading font-semibold">TOTAL</span>
              <span className="font-mono font-heading text-lg font-semibold text-primary" data-testid="quote-total">
                {fmt(totals.total)}
              </span>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button type="submit" disabled={saving} data-testid="save-quote-btn">
              <DownloadSimple size={16} weight="bold" className="mr-2" />
              {saving ? "Guardando…" : "Guardar y descargar PDF"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
